// Rendu premium des tanks (demande de Léni, 4 novembre 2026) : volume, lumière et détails ajoutés au moment de la
// cuisson (bake.ts), donc sans aucun coût par image en partie. L'ombrage ne dépend pas de la rotation (les pièces
// tournent : un reflet « en haut à gauche » tournerait avec elles) ; la lumière fixe est donnée par l'ombre portée,
// toujours décalée vers le bas à droite à l'écran. Rien ne dépasse la silhouette (hitbox et lecture inchangées), la
// couleur du camp reste visible au cœur de la tourelle.
import { BlurFilter, Container, FillGradient, Graphics } from 'pixi.js';
import type { ClassNode } from '../../../../shared/catalog/classes';
import { ART_R, drawBarrel, type Palette } from './art';

const R = ART_R;

function seeded(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    return (s >>> 0) / 4294967296;
  };
}

const treads = (g: Graphics) => {
  for (const side of [-1, 1]) g.roundRect(-0.68 * R, side * 0.66 * R - 0.19 * R, 1.36 * R, 0.38 * R, 0.12 * R);
  return g;
};
const body = (g: Graphics) => g.roundRect(-0.64 * R, -0.52 * R, 1.28 * R, 1.04 * R, 0.18 * R);
const dome = (g: Graphics) => g.circle(0, 0, 0.5 * R);

/** Couche découpée par une silhouette (masque), ajoutée au conteneur. */
function masked(parent: Container, shape: (g: Graphics) => Graphics, draw: (g: Graphics) => void): void {
  const layer = new Graphics();
  draw(layer);
  const mask = shape(new Graphics()).fill(0xffffff);
  layer.mask = mask;
  parent.addChild(layer, mask);
}

const WHITE = 0xffffff;
const BLACK = 0x000000;

/** Caisse et chenilles : occlusion sur les bords, reflet doux au centre, maillons, usure discrète. */
export function shadeHull(hull: Container, seed: number): void {
  const rnd = seeded(seed * 31 + 7);
  masked(hull, treads, (g) => {
    for (const side of [-1, 1]) {
      const y = side * 0.66 * R;
      // Maillons : plaques plus claires séparées par des rainures sombres.
      for (let i = -3; i <= 3; i++) {
        const x = i * 0.18 * R;
        g.rect(x - 0.075 * R, y - 0.17 * R, 0.15 * R, 0.34 * R).fill({ color: WHITE, alpha: 0.06 });
        g.rect(x - 0.075 * R, y - 0.17 * R, 0.15 * R, 0.05 * R).fill({ color: WHITE, alpha: 0.1 });
      }
      // Arrondi de la chenille : clair au milieu, sombre sur les flancs.
      g.rect(-0.68 * R, y - 0.04 * R, 1.36 * R, 0.08 * R).fill({ color: WHITE, alpha: 0.07 });
    }
    treads(g).stroke({ width: 5, color: BLACK, alpha: 0.25 });
  });
  masked(hull, body, (g) => {
    g.roundRect(-0.64 * R, -0.52 * R, 1.28 * R, 1.04 * R, 0.18 * R).fill(
      new FillGradient({
        type: 'radial',
        center: { x: 0.5, y: 0.5 },
        innerRadius: 0,
        outerCenter: { x: 0.5, y: 0.5 },
        outerRadius: 0.62,
        colorStops: [
          { offset: 0, color: 'rgba(255,255,255,0.2)' },
          { offset: 0.55, color: 'rgba(255,255,255,0.02)' },
          { offset: 1, color: 'rgba(0,0,0,0.16)' },
        ],
        textureSpace: 'local',
      }),
    );
    // Occlusion : bord intérieur assombri ; biseau : liseré clair juste à l'intérieur.
    body(g).stroke({ width: 6, color: BLACK, alpha: 0.22 });
    g.roundRect(-0.6 * R, -0.48 * R, 1.2 * R, 0.96 * R, 0.15 * R).stroke({ width: 1.1, color: WHITE, alpha: 0.16 });
    // Lignes de panneaux et usure (rayures fines).
    g.moveTo(-0.22 * R, -0.52 * R).lineTo(-0.22 * R, 0.52 * R).stroke({ width: 0.8, color: BLACK, alpha: 0.28 });
    g.moveTo(0.3 * R, -0.52 * R).lineTo(0.3 * R, 0.52 * R).stroke({ width: 0.8, color: BLACK, alpha: 0.22 });
    for (let k = 0; k < 4; k++) {
      const x = (rnd() - 0.5) * 1.1 * R;
      const y = (rnd() - 0.5) * 0.9 * R;
      const a = rnd() * Math.PI;
      const l = (0.08 + rnd() * 0.14) * R;
      g.moveTo(x, y).lineTo(x + Math.cos(a) * l, y + Math.sin(a) * l).stroke({ width: 0.6, color: WHITE, alpha: 0.12 });
    }
  });
}

/** Tubes en cylindres : sombres sur les bords, reflet sur l'axe ; seulement hors du dôme (qui est dessiné dessus). */
export function shadeBarrels(turret: Container, cls: ClassNode): void {
  const barrels = cls.weapon.barrels.filter((b) => !b.auto);
  if (barrels.length) {
    const white: Palette = { hull: WHITE, plate: WHITE, tread: WHITE, barrel: WHITE, band: WHITE, outline: WHITE };
    masked(
      turret,
      (g) => {
        for (const b of barrels) drawBarrel(g, b, white, WHITE);
        return g;
      },
      (g) => {
        for (const b of barrels) {
          const a = (b.angle * Math.PI) / 180;
          const cos = Math.cos(a);
          const sin = Math.sin(a);
          const w = b.width * R * 1.35;
          const off = b.offset * R;
          const L = b.length * R + 2;
          const x0 = 0.46 * R;
          // Dégradé en travers du tube (repère global du dessin : perpendiculaire à l'axe du tube).
          const px = -sin;
          const py = cos;
          const cx = -sin * off;
          const cy = cos * off;
          const grad = new FillGradient({
            type: 'linear',
            start: { x: cx - px * (w / 2), y: cy - py * (w / 2) },
            end: { x: cx + px * (w / 2), y: cy + py * (w / 2) },
            colorStops: [
              { offset: 0, color: 'rgba(0,0,0,0.34)' },
              { offset: 0.3, color: 'rgba(255,255,255,0.05)' },
              { offset: 0.42, color: 'rgba(255,255,255,0.36)' },
              { offset: 0.55, color: 'rgba(255,255,255,0.06)' },
              { offset: 1, color: 'rgba(0,0,0,0.42)' },
            ],
            textureSpace: 'global',
          });
          const pts = [
            [x0, -w / 2],
            [L, -w / 2],
            [L, w / 2],
            [x0, w / 2],
          ].flatMap(([x, y]) => [x * cos - (y + off) * sin, x * sin + (y + off) * cos]);
          g.poly(pts).fill(grad);
        }
      },
    );
  }
}

/** Dôme sphérique : centre éclairé, bord sombre, liseré de reflet. */
export function shadeDome(turret: Container): void {
  masked(turret, dome, (g) => {
    g.circle(0, 0, 0.5 * R).fill(
      new FillGradient({
        type: 'radial',
        center: { x: 0.5, y: 0.5 },
        innerRadius: 0,
        outerCenter: { x: 0.5, y: 0.5 },
        outerRadius: 0.5,
        colorStops: [
          { offset: 0, color: 'rgba(255,255,255,0.3)' },
          { offset: 0.6, color: 'rgba(255,255,255,0.04)' },
          { offset: 0.84, color: 'rgba(0,0,0,0.08)' },
          { offset: 1, color: 'rgba(0,0,0,0.34)' },
        ],
        textureSpace: 'local',
      }),
    );
    g.circle(0, 0, 0.42 * R).stroke({ width: 1.2, color: WHITE, alpha: 0.24 });
  });
}

/**
 * Cœur à la couleur du camp, redessiné au-dessus de l'ombrage (jamais assombri), avec un éclat vitré. `outline` :
 * contour du dôme ; sans `accent`, seul l'éclat est ajouté (cœur déjà dessiné au-dessus).
 */
export function glossCore(turret: Container, accent?: number, outline = 0x22272e): void {
  const g = new Graphics();
  if (accent !== undefined) {
    g.circle(0, 0, 0.3 * R).fill(accent).stroke({ width: 1.5, color: outline });
    g.circle(0, 0, 0.09 * R).fill(outline);
  }
  g.ellipse(-0.08 * R, -0.1 * R, 0.13 * R, 0.07 * R).fill({ color: WHITE, alpha: 0.45 });
  g.circle(0, 0, 0.3 * R).stroke({ width: 1, color: WHITE, alpha: 0.22 });
  turret.addChild(g);
}

/** Ombre portée douce (silhouette floutée), dessinée à part : en partie, elle ne tourne qu'avec la pièce. */
export function shadowOf(kind: 'hull' | 'turret', cls: ClassNode): Container {
  const c = new Container();
  const g = new Graphics();
  if (kind === 'hull') {
    treads(g).fill(BLACK);
    body(g).fill(BLACK);
  } else {
    const black: Palette = { hull: BLACK, plate: BLACK, tread: BLACK, barrel: BLACK, band: BLACK, outline: BLACK };
    for (const b of cls.weapon.barrels) if (!b.auto) drawBarrel(g, b, black, BLACK);
    dome(g).fill(BLACK);
  }
  g.filters = [new BlurFilter({ strength: 3, quality: 3 })];
  c.addChild(g);
  return c;
}
